<script setup lang="ts">
import type { PaymentProviderType } from "@innoclouds/api";
import { DecorIcon } from "@innoclouds/ui";
import { useTranslation } from "i18next-vue";
import { computed, ref, watch } from "vue";
import crossIcon from "#/assets/icons/cross.svg";
import { useDecorationStore } from "#/store/decoration";
import { useThemeStore } from "#/store/theme";
import BottomSheet from "#/ui/bottomSheet/BottomSheet.vue";
import { emitter } from "#/utils/emitter";
import { formatPrice } from "#/utils/i18next";
import KsoCreatedActions from "./ksoCreatedActions/KsoCreatedActions.vue";
import KsoStages from "./ksoStages/KsoStages.vue";
import type { KsoStage } from "./stages";
import { DEFAULT_TRANSITION_DURATION, KSO_SHEET_EASE, LOADER_SCREEN_WAVES } from "./stages";

interface Props {
	stage: KsoStage;
	discount: number;
	bonuses: number;
	maxBonuses: number;
	total: number;
	deliveryCost?: number | null;
	providers: PaymentProviderType[];
	currencySymbol?: string | null;
	bonusSign?: string;
	bonusesLabel?: string;
	allowDecimalBonuses?: boolean;
	bonusesAvailable?: boolean;
	payDisabled?: boolean;
	orderReady?: boolean;
	dockTo?: string;
	decoration?: string;
}

const props = withDefaults(defineProps<Props>(), {
	deliveryCost: null,
	currencySymbol: null,
	bonusSign: "",
	bonusesLabel: "",
	allowDecimalBonuses: false,
	bonusesAvailable: false,
	payDisabled: false,
	orderReady: false,
	dockTo: undefined,
	decoration: undefined,
});

const emit = defineEmits<{
	"apply-bonuses": [value: number];
	pay: [];
	"select-provider": [provider: PaymentProviderType];
	"close-methods": [];
	"cancel-creating": [];
	"bonuses-open": [];
	cancel: [];
}>();

const themeStore = useThemeStore();
const { t } = useTranslation();
const decor = useDecorationStore().screen(props.decoration);

const loaderScreen = computed(() => themeStore.data?.loader_screen);
const isImmersive = computed(() => loaderScreen.value?.type === LOADER_SCREEN_WAVES);
const hasWaves = computed(() => {
	if (props.stage === "created" || props.stage === "creating") return decor.visible("kso_waves", isImmersive.value);
	if (props.stage === "methods") return decor.visible("kso_waves", false);
	return false;
});

const isOpen = ref(false);
const isBonusesOpen = ref(false);

watch(
	() => props.stage,
	(stage, previous) => {
		isOpen.value = stage !== "hidden";
		if (stage !== previous) isBonusesOpen.value = false;
		if (stage !== "hidden" && previous === "hidden") emitter.emit("keyboard:close");
	},
	{ immediate: true },
);

watch(
	() => props.maxBonuses,
	(value) => {
		if (value <= 0) isBonusesOpen.value = false;
	},
);

const isBlocking = computed(() => props.stage === "creating" || props.stage === "methods" || (props.stage === "created" && isBonusesOpen.value));
const headerClass = computed(() => (props.stage === "methods" ? "kso_sheet_header kso_sheet_header_handle" : "kso_sheet_header"));

const bonusesButtonText = computed(() => {
	const label = props.bonusesLabel || t(`cart.button.bonuses`);
	if (!props.bonusesAvailable || props.maxBonuses <= 0 || !decor.visible("kso_bonus_amount")) return label;
	return `${label}: ${formatPrice(props.maxBonuses)}`;
});

const applyBonuses = (value: number) => {
	emit("apply-bonuses", value);
};

const toggleBonuses = () => {
	isBonusesOpen.value = !isBonusesOpen.value;
	if (isBonusesOpen.value) emit("bonuses-open");
};

const stepBack = () => {
	if (isBonusesOpen.value) {
		isBonusesOpen.value = false;
		return;
	}
	if (props.stage === "methods") emit("close-methods");
	else emit("cancel");
};

const onOverlayClick = (event: MouseEvent) => {
	if (!(event.target as HTMLElement | null)?.hasAttribute?.("data-vsbs-backdrop")) return;

	if (isBonusesOpen.value) {
		isBonusesOpen.value = false;
		return;
	}

	if (props.stage === "methods") emit("close-methods");
};
</script>

<template>
	<Teleport v-if="props.dockTo" :to="props.dockTo" defer>
		<Transition name="kso_dock">
			<div
				v-if="isOpen"
				class="kso_checkout_dock"
				:data-stage="props.stage"
				:data-waves="hasWaves"
				v-bind="decor.bind('kso_sheet', { [props.stage]: true, waves: hasWaves })"
			>
				<div :class="headerClass" v-bind="decor.bind('kso_header', { [props.stage]: true })">
					<span v-if="props.stage === 'methods' && decor.visible('kso_grip')" class="kso_sheet_grip" />
				</div>
				<div class="kso_sheet_content" v-bind="decor.bind('kso_content')">
					<KsoStages
						:stage="props.stage"
						:immersive="isImmersive"
						:order-ready="props.orderReady"
						:phrases="loaderScreen?.phrase?.created ?? null"
						:phrase-interval="loaderScreen?.phrase_interval ?? null"
						:discount="props.discount"
						:bonuses="props.bonuses"
						:max-bonuses="props.maxBonuses"
						:total="props.total"
						:delivery-cost="props.deliveryCost"
						:providers="props.providers"
						:currency-symbol="props.currencySymbol"
						:bonus-sign="props.bonusSign"
						:allow-decimal-bonuses="props.allowDecimalBonuses"
						:bonuses-open="isBonusesOpen"
						:decoration="props.decoration"
						@cancel-creating="emit('cancel-creating')"
						@apply-bonuses="applyBonuses"
						@close-bonuses="isBonusesOpen = false"
						@select-provider="(provider) => emit('select-provider', provider)"
						@close-methods="emit('close-methods')"
					/>
				</div>
				<div v-if="props.stage === 'created'" class="kso_sheet_footer" v-bind="decor.bind('kso_footer', { bonuses: isBonusesOpen })">
					<KsoCreatedActions
						:bonuses-available="props.bonusesAvailable"
						:bonuses-open="isBonusesOpen"
						:bonuses-text="bonusesButtonText"
						:total="props.total"
						:currency-symbol="props.currencySymbol"
						:pay-disabled="props.payDisabled"
						:decoration="props.decoration"
						@toggle-bonuses="toggleBonuses"
						@pay="emit('pay')"
					/>
				</div>
				<button
					v-if="(props.stage === 'created' || props.stage === 'methods') && decor.visible('kso_close', false)"
					type="button"
					class="kso_dock_close"
					:aria-label="t(`cart.button.back`)"
					v-bind="decor.bind('kso_close')"
					@click="stepBack"
				>
					<DecorIcon :icon="decor.icon('kso_close')" :fallback="crossIcon" />
				</button>
			</div>
		</Transition>
	</Teleport>
	<BottomSheet
		v-else
		v-model="isOpen"
		class="kso_checkout_sheet"
		:data-stage="props.stage"
		:data-waves="hasWaves"
		:blocking="isBlocking"
		teleport-to="#app"
		:can-swipe-close="false"
		:can-backdrop-close="false"
		:expand-on-content-drag="false"
		:duration="DEFAULT_TRANSITION_DURATION"
		:ease="KSO_SHEET_EASE"
		:max-screen-fraction="0.94"
		:header-class="headerClass"
		footer-class="kso_sheet_footer"
		content-class="kso_sheet_content"
		@click="onOverlayClick"
	>
		<template #header><span v-if="props.stage === 'methods'" class="kso_sheet_grip" /></template>
		<template #footer>
			<KsoCreatedActions
				v-if="props.stage === 'created'"
				:bonuses-available="props.bonusesAvailable"
				:bonuses-open="isBonusesOpen"
				:bonuses-text="bonusesButtonText"
				:total="props.total"
				:currency-symbol="props.currencySymbol"
				:pay-disabled="props.payDisabled"
				@toggle-bonuses="toggleBonuses"
				@pay="emit('pay')"
			/>
		</template>

		<KsoStages
			:stage="props.stage"
			:immersive="isImmersive"
			:order-ready="props.orderReady"
			:phrases="loaderScreen?.phrase?.created ?? null"
			:phrase-interval="loaderScreen?.phrase_interval ?? null"
			:discount="props.discount"
			:bonuses="props.bonuses"
			:max-bonuses="props.maxBonuses"
			:total="props.total"
			:delivery-cost="props.deliveryCost"
			:providers="props.providers"
			:currency-symbol="props.currencySymbol"
			:bonus-sign="props.bonusSign"
			:allow-decimal-bonuses="props.allowDecimalBonuses"
			:bonuses-open="isBonusesOpen"
			@cancel-creating="emit('cancel-creating')"
			@apply-bonuses="applyBonuses"
			@close-bonuses="isBonusesOpen = false"
			@select-provider="(provider) => emit('select-provider', provider)"
			@close-methods="emit('close-methods')"
		/>
	</BottomSheet>
</template>

<style lang="scss">
  @use './ksoCheckout.scss';
</style>
